import { PlayerHookType } from '@cbsinteractive/avia-js';
import { HookContextInterface } from '@cbsinteractive/avia-js/dist/types/iface/HookContextInterface';
import { IPlayerAdapterDeviceCapabilities } from 'library/ts/player/adapter/interfaces/capabilities/IPlayerAdapterDeviceCapabilities';
import { TagEvent } from 'tag/ts/tag/events/TagEvent';
import { TagEventVO } from 'tag/ts/tag/events/TagEventVO';
import { ITagAbstractClassArgument } from 'tag/ts/tag/interfaces/arguments/ITagAbstractClassArgument';
import { AbstractAviaPlayerHook } from 'tag/ts/tag/player/adapters/avia/hooks/AbstractAviaPlayerHook';

/**
 * @copyright © Paramount Streaming
 * @author J. Ortiz, Jeff Appis
 * @description Preferred Video Codec Hierarchy hook
 */
export class AviaPlayerPreferredVideoCodecHook extends AbstractAviaPlayerHook {
	hookType: PlayerHookType = PlayerHookType.RESPONSE;

	constructor( argument: ITagAbstractClassArgument ) {
		super(
			{
				...argument,
				name: 'AviaPlayerPreferredVideoCodecHook'
			}
		);

		this.tagEventsHandler( this.#onTagEvent, [
			TagEvent.ON_CREATE_PLAYER_SUCCESS
		] );
	}

	/**
	 * @description Tag application event handler
	 * @param {TagEventVO} event
	 */
	#onTagEvent = ( event: TagEventVO ): void => {
		// filter by event type
		switch ( event?.type ) {
			case TagEvent.ON_CREATE_PLAYER_SUCCESS:
				// stop listening to this event... we need it once per instance to prevent memory leak
				this.removeTagEventsHandler(
					this.#onTagEvent,
					this.tag.eventDispatcher,
					[ TagEvent.ON_CREATE_PLAYER_SUCCESS ]
				).then(
					( data ) => {
						if ( window?.SmartTag?.deviceCapabilities ) {
							this.setPreferredVideoCodecs( window.SmartTag.deviceCapabilities );
						} else {
							// fetch capabilities before starting video codec hierarchy operations
							this.tag.controller.playerManager?.playerAdapter?.getDeviceCapabilities?.()?.then( ( capabilities ) => {
								// No conversion needed - using consistent IPlayerAdapterDeviceCapabilities
								this.setPreferredVideoCodecs( capabilities );
							} );
						}
					}
				);
				break;
		}
	};

	#debugCapabilities = ( capabilities: IPlayerAdapterDeviceCapabilities ) => {
		const debugCapabilitiesOverride = this?.tag?.model?.config?.tag?.Hooks?.params?.PreferredVideoCodecHook?.debug?.capabilities;
		if ( !this.utils.Type.isNullOrUndefined( debugCapabilitiesOverride ) && !this.utils.Type.isEmpty( debugCapabilitiesOverride ) ) {
			this?.logger?.log( 'overriding capabilities', 'debugCapabilitiesOverride', debugCapabilitiesOverride );
			capabilities = {
				...capabilities,
				...debugCapabilitiesOverride
			};
		}

		return capabilities;
	};

	setPreferredVideoCodecs = ( capabilities: IPlayerAdapterDeviceCapabilities ) => {
		const preferredVideoCodecs: string[] = [];
		const isPremiumVideoDisabled: boolean = this.tag?.params?.USER?.settings?.disablePremiumVideo;

		this?.logger?.log( 'setPreferredVideoCodecs', 'capabilities', capabilities, 'isPremiumVideoDisabled', isPremiumVideoDisabled );

		capabilities = this.#debugCapabilities( capabilities );

		if ( !isPremiumVideoDisabled ) {
			if ( capabilities?.supportsUhd && !capabilities?.supportsDolbyVision && !capabilities?.supportsHdr10 ) {
				/* ---- Everything is FALSE, but 4K/UHD is TRUE ----
				* registerHook and see callback
				*/
				this.registerHook();
				return;
			}

			if ( capabilities?.supportsDolbyVision ) {
				/* ---- DolbyVision is TRUE ---- (Only on LG TVs)
				* This if statement is only applicable on LG TV, because unfortunately
				* Samsung APIs don't provide us information about Dolby Vision support. So in
				* other words, on Samsung this will always be FALSE, even if it is supported.
				* We will have to figure out another way to determine Dolby Vision support
				* check on Samsung Tizen TVs.
				*/
				this?.logger?.log( 'This TV supports Dolby Vision. Adding dvh codec.' );
				preferredVideoCodecs.push( 'dvh' );
			}

			if ( capabilities?.supportsHdr10 ) {
				/* ---- HDR 10 is TRUE ----
				* In this else if block, we found out that HDR 10 is supported so
				* we are setting HVC as a preferred codec.
				*/
				this?.logger?.log( 'This TV supports hdr10. Adding hvc and hev codecs.' );
				preferredVideoCodecs.push( 'hev1.2' );
				preferredVideoCodecs.push( 'hvc1.2' );
				preferredVideoCodecs.push( 'hev1.1' );
				preferredVideoCodecs.push( 'hvc1.1' );
			}
		} else {
			this?.logger?.log( 'setPreferredVideoCodecs', 'Premium video is disabled, skipping codec modifications' );
		}

		// always add avc to the end as a default
		this?.logger?.log( 'Adding avc codec.' );
		preferredVideoCodecs.push( 'avc' );

		this.params.CONTENT.resource = {
			...this.params?.CONTENT?.resource,
			playback: {
				...this.params?.CONTENT?.resource?.playback,
				preferredVideoCodecs
			}
		};
	};

	callback = ( context: HookContextInterface<any> ): void => {
		/*
		* As a final option, since neither HDR 10 nor Dolby Vision are
		* supported, we need to look up the manifest to determine if its
		* HDR or SDR type.
		*/
		if ( context.value.url.indexOf( '.mpd' ) === -1 ) {
			return;
		}

		const response = context?.value;

		if ( this.utils.Type.isNullOrUndefined( response ) ) {
			this?.logger?.warn( 'there was not a response in the manifest', response?.url );
			return;
		}

		this.#useManifestToConfigurePreferredVideoCodecs( response?.data );
	};

	#useManifestToConfigurePreferredVideoCodecs = ( manifestContents: string ) => {
		if ( this.utils.Type.isNullOrUndefined( manifestContents ) ) {
			this?.logger?.warn( 'manifest contents are empty', manifestContents );
			return;
		}

		const manifestCodecs = this.#getManifestVideoCodecs( manifestContents );

		// This is where we make a decision based on the codec type. Set HVC as
		// a preferred video codec only if manifest includes `hvc1.1` type.
		const SDR_4K_CODEC_HVC = 'hvc1.1';
		const SDR_4K_CODEC_HEV = 'hev1.1';
		const HDR_4K_CODEC_HVC = 'hvc1.2';
		const HDR_4K_CODEC_HEV = 'hev1.2';

		if ( manifestCodecs.indexOf( SDR_4K_CODEC_HVC ) !== -1 || manifestCodecs.indexOf( SDR_4K_CODEC_HEV ) !== -1 ) {
			this?.logger?.log( 'This manifest has hvc1.1. Setting codec to HVC =>' );
			this.#configureShakaPlayer( [ 'hev1.1', 'hvc1.1', 'avc' ] );
		} else if ( manifestCodecs.indexOf( HDR_4K_CODEC_HVC ) !== -1 || manifestCodecs.indexOf( HDR_4K_CODEC_HEV ) !== -1 ) {
			this?.logger?.log( 'This manifest has hvc1.2. Setting codec to AVC =>' );
			this.#configureShakaPlayer( [ 'avc' ] );
		} else {
			this?.logger?.log( 'no hvc content in manifest, setting default preferred codecs' );
			this.#configureShakaPlayer( [ 'avc' ] );
		}
	};

	#getManifestVideoCodecs( manifestContents ) {
		const videoCodecs = {};
		try {
			const parser = new DOMParser();
			const xml = parser.parseFromString( manifestContents, 'text/xml' );
			const representations = xml.querySelectorAll( 'Representation' );

			// Look up codecs field in representation sets
			representations.forEach( ( representation ) => {
				const codecs = representation.getAttribute( 'codecs' );

				if ( this.utils.Type.isNullOrUndefined( codecs ) ) {
					return;
				}

				// We don't care about these codecs, so just ignore these
				const irrelevant = [ 'ec-3', 'mp4a', 'wvtt' ];
				const hasIrrelevant = irrelevant.some( ( codec ) => {
					return codecs.indexOf( codec ) !== -1;
				} );

				if ( hasIrrelevant ) {
					// skip irrelevant representations
					return;
				}

				this?.logger?.log( 'relevant codecs in representations found =>', codecs );

				videoCodecs[ codecs ] = true;
			} );

		} catch ( error ) {
			this?.logger?.log( 'there was an error parsing manifest =>', error );
		}

		const uniqueVideoCodecs = Object.keys( videoCodecs );

		this?.logger?.log( '#getManifestVideoCodecs uniqueVideoCodecs', uniqueVideoCodecs );

		return uniqueVideoCodecs;
	}

	#configureShakaPlayer( codecsPayload: string[] ) {
		const shakaAdapter = this.tag?.controller?.playerManager?.player?.adaptedPlayer?.getAdapter( 'playback' );

		if ( !shakaAdapter ) {
			this?.logger?.log( 'unable to retrieve playback adapter' );
			return;
		}

		// directly configure shaka player
		shakaAdapter?.player?.configure( {
			preferredVideoCodecs: codecsPayload
		} );

		// set player config of shaka adapter
		// NOTE: in looking through Avia's Shaka Adapter I have observed that the
		// adapter maintains its own copy of the Shaka player's initial configuration,
		// which is only pulled from the Shaka player once, during adapter construction.
		// The adapter modifies this configuration and continually applies it to the
		// Shaka player, which causes any direct configuration to Shaka player from
		// the outside world to be overwritten.
		shakaAdapter.playerConfig = {
			...shakaAdapter?.playerConfig,
			preferredVideoCodecs: codecsPayload
		};
	}
}